import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  useTheme,
  Fade,
  SelectChangeEvent,
} from "@mui/material";
import {
  Send as SendIcon,
  BugReport as BugReportIcon,
  Lightbulb as LightbulbIcon,
  Warning as WarningIcon,
  Help as HelpIcon,
  ChatBubble as ChatBubbleIcon,
} from "@mui/icons-material";
import { PageContainer, ModernCard } from "../components/common/StyledComponents";
import { useSendContact } from "../hooks/feedback";
import { ContactType } from "../api/feedback";

const FeedbackPage: React.FC = () => {
  const theme = useTheme();
  const [type, setType] = useState<ContactType>("OTHER");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const { mutate: sendContact, isPending, isError, error } = useSendContact();

  const feedbackTypes = [
    {
      value: "BUG_REPORT" as ContactType,
      label: "Bug Report",
      icon: <BugReportIcon />,
      color: theme.palette.error.main,
      description: "Report a bug or technical issue",
    },
    {
      value: "FEATURE_REQUEST" as ContactType,
      label: "Feature Request",
      icon: <LightbulbIcon />,
      color: theme.palette.info.main,
      description: "Suggest a new feature or enhancement",
    },
    {
      value: "COMPLAINT" as ContactType,
      label: "Complaint",
      icon: <WarningIcon />,
      color: theme.palette.warning.main,
      description: "Share concerns or complaints",
    },
    {
      value: "QUESTION" as ContactType,
      label: "Question",
      icon: <HelpIcon />,
      color: theme.palette.success.main,
      description: "Ask a question or get help",
    },
    {
      value: "OTHER" as ContactType,
      label: "Other",
      icon: <ChatBubbleIcon />,
      color: theme.palette.secondary.main,
      description: "General feedback or comments",
    },
  ];

  const handleTypeChange = (event: SelectChangeEvent) => {
    setType(event.target.value as ContactType);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSuccessMessage("");

    sendContact(
      { type, subject, message },
      {
        onSuccess: () => {
          setSuccessMessage("Thank you for contacting us! We'll get back to you soon.");
          setSubject("");
          setMessage("");
          setType("OTHER");
        },
      }
    );
  };

  const isFormValid = subject.trim() !== "" && message.trim() !== "";

  const selectedTypeInfo = feedbackTypes.find((t) => t.value === type);

  return (
    <PageContainer>
      <Container maxWidth="md">
        <Box py={{ xs: 4, md: 6 }}>
          {/* Header */}
          <Fade in timeout={600}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 1,
                  fontSize: { xs: "1.75rem", md: "2.5rem" },
                }}
              >
                Contact Us
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.95rem", md: "1.1rem" } }}
              >
                We'd love to hear from you! Share your thoughts, report issues, or ask questions.
              </Typography>
            </Box>
          </Fade>

          {/* Feedback Form */}
          <ModernCard
            sx={{
              p: { xs: 3, md: 4 },
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <form onSubmit={handleSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Success Message */}
                {successMessage && (
                  <Alert severity="success" onClose={() => setSuccessMessage("")}>
                    {successMessage}
                  </Alert>
                )}

                {/* Error Message */}
                {isError && (
                  <Alert severity="error">
                    {(error as any)?.message || "Failed to send message. Please try again."}
                  </Alert>
                )}

                {/* Message Type */}
                <FormControl fullWidth>
                  <InputLabel id="feedback-type-label">Message Type</InputLabel>
                  <Select
                    labelId="feedback-type-label"
                    id="feedback-type"
                    value={type}
                    label="Message Type"
                    onChange={handleTypeChange}
                    sx={{
                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      },
                    }}
                  >
                    {feedbackTypes.map((feedbackType) => (
                      <MenuItem key={feedbackType.value} value={feedbackType.value}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              color: feedbackType.color,
                            }}
                          >
                            {feedbackType.icon}
                          </Box>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {feedbackType.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {feedbackType.description}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Selected Type Info */}
                {selectedTypeInfo && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      background: selectedTypeInfo.color + "10",
                      border: `1px solid ${selectedTypeInfo.color}30`,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ color: selectedTypeInfo.color, display: "flex" }}>
                      {selectedTypeInfo.icon}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {selectedTypeInfo.description}
                    </Typography>
                  </Box>
                )}

                {/* Subject */}
                <TextField
                  fullWidth
                  label="Subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  placeholder="Brief summary of your message"
                  inputProps={{ maxLength: 200 }}
                  helperText={`${subject.length}/200 characters`}
                />

                {/* Message */}
                <TextField
                  fullWidth
                  label="Message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  multiline
                  rows={8}
                  placeholder="Provide detailed information..."
                  inputProps={{ maxLength: 2000 }}
                  helperText={`${message.length}/2000 characters`}
                />

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={!isFormValid || isPending}
                  startIcon={isPending ? <CircularProgress size={20} /> : <SendIcon />}
                  sx={{
                    py: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    "&:hover": {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                      transform: "translateY(-2px)",
                      boxShadow: `0 8px 16px ${theme.palette.primary.main}40`,
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  {isPending ? "Sending..." : "Send Message"}
                </Button>
              </Box>
            </form>
          </ModernCard>

          {/* Additional Info */}
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              We value your input and will respond as soon as possible. Thank you for reaching out!
            </Typography>
          </Box>
        </Box>
      </Container>
    </PageContainer>
  );
};

export default FeedbackPage;

